import React, { useEffect, useState } from 'react';
import {
  Loader2, Save, Percent, Tag, Hash, ToggleLeft, ToggleRight,
  Clock, Banknote, Star, Truck, ShoppingCart, Zap, Gift, Shield,
  Award, Flame, Heart, Plus, Pencil, Eye, EyeOff, Trash2, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import api from '../../lib/api';

const DEFAULT_CARDS = [
  { title: 'Entrega el mismo día', desc: 'Recibe tu pedido en 20 min a 3 horas según tu zona', icon: 'clock', gradient: 'from-orange-500 to-red-600' },
  { title: 'Pago en efectivo o transferencia', desc: 'Elige cómo prefieres pagar', icon: 'banknote', gradient: 'from-purple-600 to-pink-600' },
  { title: 'Combina y ahorra', desc: 'Lleva 3 vapes y obtén un precio especial', icon: 'percent', gradient: 'from-cyan-500 to-blue-600' },
  { title: 'Nuevos sabores cada semana', desc: 'Siempre tenemos lo último en vapes', icon: 'star', gradient: 'from-lime-400 to-emerald-600' },
];

const ICON_OPTIONS = [
  { name: 'clock', Icon: Clock, label: 'Reloj' },
  { name: 'banknote', Icon: Banknote, label: 'Billete' },
  { name: 'percent', Icon: Percent, label: '% Descuento' },
  { name: 'star', Icon: Star, label: 'Estrella' },
  { name: 'truck', Icon: Truck, label: 'Camión' },
  { name: 'cart', Icon: ShoppingCart, label: 'Carrito' },
  { name: 'zap', Icon: Zap, label: 'Rayo' },
  { name: 'gift', Icon: Gift, label: 'Regalo' },
  { name: 'shield', Icon: Shield, label: 'Escudo' },
  { name: 'award', Icon: Award, label: 'Medalla' },
  { name: 'flame', Icon: Flame, label: 'Fuego' },
  { name: 'heart', Icon: Heart, label: 'Corazón' },
];

const GRADIENT_OPTIONS = [
  { value: 'from-orange-500 to-red-600', label: 'Naranja / Rojo' },
  { value: 'from-purple-600 to-pink-600', label: 'Morado / Rosa' },
  { value: 'from-cyan-500 to-blue-600', label: 'Cyan / Azul' },
  { value: 'from-lime-400 to-emerald-600', label: 'Lima / Verde' },
  { value: 'from-amber-400 to-orange-600', label: 'Ámbar / Naranja' },
  { value: 'from-rose-500 to-pink-600', label: 'Rosa / Fucsia' },
  { value: 'from-violet-500 to-purple-700', label: 'Violeta / Morado' },
  { value: 'from-teal-400 to-cyan-600', label: 'Teal / Cyan' },
];

const DEFAULT_RULES = [
  {
    id: 'rule_legacy',
    name: '3+ desechables — 10%',
    condition_type: 'min_qty',
    qty: 3,
    category: 'desechable',
    discount_type: 'percentage',
    discount_value: 10,
    active: true,
    hidden: false,
  },
  {
    id: 'rule_2pcs_100mxn',
    name: '2 piezas exactas — $100 MXN',
    condition_type: 'exact_qty',
    qty: 2,
    category: '',
    discount_type: 'fixed_mxn',
    discount_value: 100,
    active: true,
    hidden: false,
  },
];

const EMPTY_RULE = {
  id: '',
  name: '',
  condition_type: 'min_qty',
  qty: 1,
  category: '',
  discount_type: 'percentage',
  discount_value: 10,
  active: true,
  hidden: false,
};

const PromotionsSection = () => {
  const [s, setS] = useState({
    cart_promotions: DEFAULT_RULES,
    promo_cards: DEFAULT_CARDS,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [editIdx, setEditIdx] = useState(-1);

  useEffect(() => {
    api.get('/admin/settings').then((r) => {
      const d = r.data || {};
      setS({
        cart_promotions: Array.isArray(d.cart_promotions) && d.cart_promotions.length > 0
          ? d.cart_promotions
          : DEFAULT_RULES,
        promo_cards: Array.isArray(d.promo_cards) && d.promo_cards.length === 4 ? d.promo_cards : DEFAULT_CARDS,
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/settings', {
        cart_promotions: s.cart_promotions,
        promo_cards: s.promo_cards,
      });
      toast({ title: '¡Guardado!', description: 'Promociones actualizadas.' });
    } catch {
      toast({ title: 'Error', description: 'No se pudo guardar.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const updateCard = (idx, patch) => {
    const next = [...s.promo_cards];
    next[idx] = { ...next[idx], ...patch };
    setS((prev) => ({ ...prev, promo_cards: next }));
  };

  const toggleActive = (idx) => {
    const next = s.cart_promotions.map((r, i) => i === idx ? { ...r, active: !r.active } : r);
    setS((prev) => ({ ...prev, cart_promotions: next }));
  };

  const toggleHidden = (idx) => {
    const next = s.cart_promotions.map((r, i) => i === idx ? { ...r, hidden: !r.hidden } : r);
    setS((prev) => ({ ...prev, cart_promotions: next }));
  };

  const openEdit = (idx) => {
    if (idx === -1) {
      setEditRule({ ...EMPTY_RULE, id: `rule_${Date.now()}` });
    } else {
      setEditRule({ ...s.cart_promotions[idx] });
    }
    setEditIdx(idx);
  };

  const closeEdit = () => { setEditRule(null); setEditIdx(-1); };

  const saveRule = () => {
    if (!editRule.name.trim()) {
      toast({ title: 'Error', description: 'El nombre de la regla es requerido.', variant: 'destructive' });
      return;
    }
    let next;
    if (editIdx === -1) {
      next = [...s.cart_promotions, editRule];
    } else {
      next = s.cart_promotions.map((r, i) => i === editIdx ? editRule : r);
    }
    setS((prev) => ({ ...prev, cart_promotions: next }));
    closeEdit();
  };

  const deleteRule = (idx) => {
    const next = s.cart_promotions.filter((_, i) => i !== idx);
    setS((prev) => ({ ...prev, cart_promotions: next }));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>;

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-2xl font-black text-white">Promociones</h2>
        <p className="text-white/50 text-sm mt-1">Edita las tarjetas de la página de Ofertas y configura el descuento automático del carrito.</p>
      </div>

      {/* ── Tarjetas de ofertas ── */}
      <div className="bg-neutral-950 border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-4 h-4 text-orange-400" />
          <h3 className="text-white font-bold">Tarjetas de la página de Ofertas</h3>
        </div>
        <p className="text-white/40 text-xs mb-4">Edita el título, descripción, ícono y color de cada tarjeta.</p>
        <div className="space-y-4">
          {s.promo_cards.map((card, idx) => {
            const Icon = ICON_OPTIONS.find((o) => o.name === card.icon)?.Icon || Star;
            return (
              <div key={idx} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${card.gradient} flex items-center justify-center shrink-0`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs text-white/50 font-bold uppercase tracking-wider">Tarjeta {idx + 1}</span>
                </div>
                <Input
                  value={card.title}
                  onChange={(e) => updateCard(idx, { title: e.target.value })}
                  placeholder="Título"
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid={`admin-promo-card-title-${idx}`}
                />
                <Input
                  value={card.desc}
                  onChange={(e) => updateCard(idx, { desc: e.target.value })}
                  placeholder="Descripción"
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid={`admin-promo-card-desc-${idx}`}
                />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1.5">Ícono</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ICON_OPTIONS.map(({ name, Icon: Ic, label }) => (
                      <button
                        key={name} type="button" title={label}
                        onClick={() => updateCard(idx, { icon: name })}
                        className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${
                          card.icon === name
                            ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                            : 'border-white/10 bg-white/5 text-white/40 hover:border-orange-500/40 hover:text-white/70'
                        }`}
                      >
                        <Ic className="w-3.5 h-3.5" />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1.5">Color</p>
                  <div className="flex flex-wrap gap-2">
                    {GRADIENT_OPTIONS.map((g) => (
                      <button
                        key={g.value} type="button" title={g.label}
                        onClick={() => updateCard(idx, { gradient: g.value })}
                        className={`w-8 h-8 rounded-lg bg-gradient-to-br ${g.value} transition-all ${
                          card.gradient === g.value ? 'ring-2 ring-white scale-110' : 'hover:scale-105 opacity-70 hover:opacity-100'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Descuento automático del carrito ── */}
      <div className="bg-neutral-950 border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Percent className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Descuento automático del carrito</h3>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => openEdit(-1)}
            data-testid="admin-promo-add-rule-btn"
            className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10 text-xs h-8 gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Nueva regla
          </Button>
        </div>
        <p className="text-white/40 text-xs">Las reglas se evalúan en orden. Se aplica la primera que coincida con el carrito.</p>

        {s.cart_promotions.length === 0 && (
          <p className="text-white/30 text-sm text-center py-8">Sin reglas. Crea una con "+ Nueva regla".</p>
        )}

        <div className="space-y-2">
          {s.cart_promotions.map((rule, idx) => {
            const condLabel = rule.condition_type === 'exact_qty'
              ? `= ${rule.qty} pcs exactas`
              : `≥ ${rule.qty} pcs`;
            const discLabel = rule.discount_type === 'fixed_mxn'
              ? `$${rule.discount_value} MXN`
              : `${rule.discount_value}%`;
            const catLabel = rule.category ? rule.category : 'todos';
            return (
              <div
                key={rule.id || idx}
                data-testid={`admin-promo-rule-${idx}`}
                className={`rounded-xl border p-3.5 transition-all ${rule.hidden ? 'opacity-40' : ''} ${rule.active ? 'border-orange-500/25 bg-orange-500/5' : 'border-white/8 bg-white/[0.02]'}`}
              >
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => toggleActive(idx)}
                    data-testid={`admin-promo-rule-toggle-${idx}`}
                    title={rule.active ? 'Desactivar' : 'Activar'}
                    className="transition-transform active:scale-95 shrink-0"
                  >
                    {rule.active
                      ? <ToggleRight className="w-9 h-9 text-orange-500" />
                      : <ToggleLeft className="w-9 h-9 text-white/30" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm truncate ${rule.active ? 'text-white' : 'text-white/45'}`}>
                      {rule.name}
                    </p>
                    <p className="text-[11px] text-white/40 mt-0.5 font-mono">
                      {condLabel} · cat: <span className="text-white/60">{catLabel}</span>
                      {' '}→ <span className="text-orange-400 font-semibold">{discLabel}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEdit(idx)}
                      title="Editar"
                      data-testid={`admin-promo-rule-edit-${idx}`}
                      className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:border-orange-500/40 hover:text-orange-400 text-white/40 flex items-center justify-center transition-all"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleHidden(idx)}
                      title={rule.hidden ? 'Mostrar' : 'Ocultar en panel'}
                      data-testid={`admin-promo-rule-hide-${idx}`}
                      className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:border-white/30 text-white/40 hover:text-white/70 flex items-center justify-center transition-all"
                    >
                      {rule.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRule(idx)}
                      title="Eliminar"
                      data-testid={`admin-promo-rule-delete-${idx}`}
                      className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:border-rose-500/40 hover:text-rose-400 text-white/40 flex items-center justify-center transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Modal edición de regla ── */}
      {editRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="admin-promo-rule-modal">
          <div className="bg-neutral-950 border border-white/15 rounded-2xl p-6 w-full max-w-md space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-black text-lg">{editIdx === -1 ? 'Nueva regla' : 'Editar regla'}</h4>
              <button onClick={closeEdit} className="text-white/40 hover:text-white/70 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5 block">Nombre de la regla</label>
              <Input
                value={editRule.name}
                onChange={(e) => setEditRule((r) => ({ ...r, name: e.target.value }))}
                placeholder="Ej: 3+ desechables 10%"
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="admin-promo-rule-name-input"
              />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5 block">Tipo de condición</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { val: 'min_qty', label: 'Mínimo de X piezas (≥)' },
                  { val: 'exact_qty', label: 'Exactamente X piezas (=)' },
                ].map(({ val, label }) => (
                  <button
                    key={val} type="button"
                    onClick={() => setEditRule((r) => ({ ...r, condition_type: val }))}
                    data-testid={`admin-promo-cond-${val}`}
                    className={`rounded-lg border px-3 py-2.5 text-xs font-bold transition-all text-left ${
                      editRule.condition_type === val
                        ? 'border-orange-500 bg-orange-500/15 text-orange-300'
                        : 'border-white/10 bg-white/5 text-white/50 hover:border-orange-500/40'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5 flex items-center gap-1">
                  <Hash className="w-3 h-3" /> Cantidad
                </label>
                <Input
                  type="number" min={1} max={999}
                  value={editRule.qty}
                  onChange={(e) => setEditRule((r) => ({ ...r, qty: Number(e.target.value) }))}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="admin-promo-rule-qty-input"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Categoría
                </label>
                <Input
                  value={editRule.category}
                  onChange={(e) => setEditRule((r) => ({ ...r, category: e.target.value }))}
                  placeholder="vacío = todos"
                  className="bg-white/5 border-white/10 text-white text-sm placeholder:text-white/25"
                  data-testid="admin-promo-rule-category-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5 block">Tipo de descuento</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { val: 'percentage', label: 'Porcentaje (%)' },
                  { val: 'fixed_mxn', label: 'Pesos fijos ($MXN)' },
                ].map(({ val, label }) => (
                  <button
                    key={val} type="button"
                    onClick={() => setEditRule((r) => ({ ...r, discount_type: val }))}
                    data-testid={`admin-promo-disc-${val}`}
                    className={`rounded-lg border px-3 py-2.5 text-xs font-bold transition-all text-left ${
                      editRule.discount_type === val
                        ? 'border-orange-500 bg-orange-500/15 text-orange-300'
                        : 'border-white/10 bg-white/5 text-white/50 hover:border-orange-500/40'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5 block">
                {editRule.discount_type === 'fixed_mxn' ? 'Monto fijo ($MXN)' : 'Porcentaje (%)'}
              </label>
              <Input
                type="number" min={1}
                value={editRule.discount_value}
                onChange={(e) => setEditRule((r) => ({ ...r, discount_value: Number(e.target.value) }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="admin-promo-rule-value-input"
              />
              <p className="text-white/30 text-[11px] mt-1.5">
                {editRule.discount_type === 'fixed_mxn'
                  ? `Se restarán $${editRule.discount_value} MXN fijos del total.`
                  : `Se aplicará ${editRule.discount_value}% sobre los productos que califiquen.`}
              </p>
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                onClick={saveRule}
                data-testid="admin-promo-rule-save-btn"
                className="bg-orange-500 hover:bg-orange-600 text-black font-black flex-1"
              >
                {editIdx === -1 ? 'Agregar regla' : 'Guardar cambios'}
              </Button>
              <Button
                variant="outline" onClick={closeEdit}
                className="border-white/15 text-white/60 hover:bg-white/5 flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button
        onClick={save}
        disabled={saving}
        data-testid="admin-promo-save-btn"
        className="bg-orange-500 hover:bg-orange-600 text-black font-black w-full max-w-2xl"
      >
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : <><Save className="w-4 h-4 mr-2" /> Guardar cambios</>}
      </Button>
    </div>
  );
};

export default PromotionsSection;
